import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, MailMinus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, no-auth page reached via the unsubscribe link in every contact email.
 * One-click self-removal from the sender's contact list.
 */
export default function RemoveMe() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/contacts/remove/${token}`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, valid: false }));
  }, [token]);

  const remove = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/contacts/remove/${token}`);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Couldn't remove");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return <Shell><div className="text-ink-500 text-sm">Looking up that link…</div></Shell>;
  }

  if (!state.valid) {
    return (
      <Shell>
        <div className="flex items-start gap-3" data-testid="remove-invalid">
          <AlertCircle className="text-amber-deep mt-1" size={20} />
          <div>
            <h2 className="font-heading text-xl font-semibold">This link is no longer valid.</h2>
            <p className="text-sm text-ink-600 mt-1">It may have already been used, or the sender removed you from their contacts.</p>
          </div>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="flex items-start gap-3" data-testid="remove-success">
          <CheckCircle2 className="text-sage-600 mt-1" size={20} />
          <div>
            <h2 className="font-heading text-xl font-semibold">Done.</h2>
            <p className="text-sm text-ink-600 mt-1">{state.sender_name} can no longer email you via AMDS. Your email has been removed from their contacts.</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-start gap-3" data-testid="remove-confirm">
        <MailMinus className="text-terracotta-600 mt-1" size={20} />
        <div className="flex-1">
          <h2 className="font-heading text-xl font-semibold">Stop hearing from {state.sender_name} via AMDS?</h2>
          <p className="text-sm text-ink-600 mt-1">
            We'll remove <strong>{state.recipient_email}</strong> from {state.sender_name}'s personal contacts on AMDS.
            They won't be able to email you through AMDS again unless you ask them to add you back.
          </p>
          {error && <div className="text-sm text-terracotta-600 mt-2">{error}</div>}
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              onClick={remove}
              disabled={busy}
              className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white px-5 py-2.5 font-medium transition-all"
              data-testid="remove-confirm-btn"
            >
              {busy ? "Removing…" : "Yes, remove me"}
            </button>
            <Link to="/" className="btn-secondary justify-center" data-testid="remove-cancel-link">
              No, keep me
            </Link>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ivory-50 grid place-items-center p-6">
      <div className="card-base max-w-xl w-full p-6 sm:p-8" data-testid="remove-shell">
        <div className="label-overline">AMDS · Unsubscribe</div>
        {children}
      </div>
    </div>
  );
}
